import { Box, Text, Title, Paper, Group, Stack, RingProgress } from "@mantine/core";
import {
  CombinedLazyEyeRisk,
  LazyEyeRiskDriver,
  vaInterocularLines,
} from "../utils/lazyEyeRiskModel";
import { RISK_LEVEL_COLOR } from "@constants/resultColors";
import { RiskLevel } from "../utils/severity";

interface LazyEyeOverallAssessmentCardProps {
  combined: CombinedLazyEyeRisk;
  vaInterocDiffLogMar: number | null;
}

function buildExplanation(args: {
  level: RiskLevel;
  vaInterocDiffLogMar: number | null;
  vaSupportsAmblyopia: boolean;
  driver: LazyEyeRiskDriver;
}): string {
  const { level, vaInterocDiffLogMar, vaSupportsAmblyopia, driver } = args;

  if (level === "high") {
    if (vaSupportsAmblyopia && vaInterocDiffLogMar != null) {
      const lines = vaInterocularLines(vaInterocDiffLogMar);
      return `${lines}-line interocular acuity gap — at the standard amblyopia threshold. Prompt evaluation recommended.`;
    }
    if (driver === "cover-test") {
      return "Manifest deviation in the strabismus referral range. Comprehensive exam recommended.";
    }
    return "Combined findings reach the follow-up threshold. Consult an eye care provider.";
  }

  if (level === "moderate") {
    if (driver === "va-interocular") {
      return "Small interocular acuity difference — below the amblyopia cutoff but worth monitoring.";
    }
    if (driver === "cover-test") {
      return "Mild alignment deviation — below referral threshold. Re-check at next routine exam.";
    }
    if (driver === "phoria") {
      return "Phoria outside the comfortable range. Persistent findings warrant a vision exam.";
    }
    return "Some results outside the ideal range. Regular monitoring recommended.";
  }

  return "All findings within the expected range. Continue routine eye care.";
}

export function LazyEyeOverallAssessmentCard({
  combined,
  vaInterocDiffLogMar,
}: LazyEyeOverallAssessmentCardProps) {
  const color = RISK_LEVEL_COLOR[combined.level];
  const explanation = buildExplanation({
    level: combined.level,
    vaInterocDiffLogMar,
    vaSupportsAmblyopia: combined.vaSupportsAmblyopia,
    driver: combined.driver,
  });

  return (
    <Paper shadow="sm" p="sm" radius="md" mb="sm" withBorder>
      <Group justify="space-between" align="flex-start">
        <Box style={{ flex: 1 }}>
          <Title order={3} mb="xs">
            Overall Assessment
          </Title>
          <Text size="md" mb="sm" c="dimmed">
            {explanation}
          </Text>
        </Box>

        <Stack align="center" gap={2} style={{ flexShrink: 0 }}>
          <Text size="xs" c="dimmed">
            Risk Score
          </Text>
          <RingProgress
            size={100}
            thickness={10}
            sections={[{ value: combined.percentage, color }]}
            label={
              <Text size="xl" c={`${color}.6`} fw={700} ta="center">
                {combined.percentage}
              </Text>
            }
          />
          <Text size="xs" c="dimmed">
            out of 100
          </Text>
        </Stack>
      </Group>
    </Paper>
  );
}
